/**
 * Centralized feature flag name constants.
 *
 * This module provides a single source of truth for feature flag names
 * used throughout the frontend. Using constants prevents typos and
 * makes it easy to find all usages of a particular flag.
 */

export const FeatureFlags = {
  INPUT_GUARDRAILS: 'input_guardrails',
  RAG_ENABLED: 'RAG_enabled',
  WORKFLOWS_CHAT_ENABLED: 'workflows_chat_enabled',
  WORKFLOWS_FULL_ENABLED: 'workflows_full_enabled',
  AGENT_CREATION_ENABLED: 'agent_creation_enabled',
  APP_COMPANION_ENABLED: 'app_companion_enabled',
  OCP_TOOLS_ENABLED: 'ocp_tools_enabled',
  EXTERNAL_INTEGRATIONS_ENABLED: 'external_integrations_enabled',
  HEADLESS_ENABLED: 'headless_enabled',
  COLLABORATION_ENABLED: 'collaboration_enabled',
  ALERTING_ENABLED: 'alerting_enabled',
  CREDIT_LIMITING_ENABLED: 'credit_limiting_enabled',
  PRIVATE_AGENTS_ENABLED: 'private_agents_enabled',
  STICKYBOARD: 'stickyboard',

  // Existing flags (for union checks with new flags)
  APP_COMPANION: 'app_companion',
  PUBLISHER: 'publisher',
  AGENT_RESUME_ENABLED: 'agent_resume_enabled',
  CONFIGURED_PROMPT: 'configured_prompt',
  VOICE_DICTATION: 'voice_dictation',
  PROTEUS_UI: 'proteus_ui',
  CONNECTORS: 'connectors',
  PLUGINS: 'plugins',
  DIRECTORY: 'directory',

  // ACL / Permissions
  PERMISSION: 'permission',

  // SDK / Developer
  AGENT_SDK: 'agent_sdk',
  // Settings › Developer › A2A — surfaces the per-instance A2A agent-card
  // URL so users can wire their instance into remote A2A clients
  // (Agentspace, custom MCP-style clients).
  A2A: 'a2a',

  // VAU (Virtual Agentic User). The VAU *surface* (list, detail, switcher,
  // nav) is gated on the `vau_enabled` instance capability — NOT this flag.
  // This flag survives only for the sub-features still rolling out behind it:
  // its `can_create_vau` variable gates create-from-scratch/import, and its
  // `slack_im_ability` variable gates the Slack ability tools.
  VAU: 'vau',

  // Artifacts — gates the "Stack elements" toggle that folds the Artifacts
  // tab into per-Space stacks (stacked list + deck grid views).
  ARTIFACTS_GROUPING: 'artifacts-grouping',

  // MISC
  CORS_DOMAIN: 'cors_domain',
  NOTIFICATIONS: 'notifications',
  MESSAGING_ENABLED: 'messaging_enabled',
  SKILL_UX: 'skill_ux',
  WORKFLOW_UX: 'workflow_ux',
  // Instance-wide defaults an admin sets once in Settings, each variable
  // gating one of them. See ManageInstanceDefaultsVariables below.
  MANAGE_INSTANCE_DEFAULTS: 'manage_instance_defaults',

  // Feature flag to enable the CODE step type in workflow builder (CE sandbox execution)
  CODE_STEP_ENABLED: 'code_step_enabled',

  // Reminder + Monitor platform capability. Gates the Hypatia
  // /api/v1/agent-monitors endpoints + the six TMS-registered tools
  // (Remind / CancelReminder / ListReminders / StartMonitor / StopMonitor /
  // ListMonitors) + the system-prompt guidance blurb. No UI surface in
  // this PR — constant lands so future frontend work references it
  // consistently. See docs/tech-spec/agent-framework/monitor-remind.md.
  REMIND_MONITOR: 'remind-monitor',

  // Brands. The Optimizely integration key predates the product rename.
  BRANDS: 'brand_packs',

  // Desktop. Gates desktop-scoped tool registries on web AND the "download the
  // desktop app" surfaces (dismissible banner + Settings › Appearance card).
  // No effect inside the desktop shell (download surfaces self-hide there).
  DESKTOP_TOOLS_ENABLED: 'desktop_tools_enabled',

  // Tools Management
  MANAGE_SYSTEM_TOOLS: 'manage_system_tools',
  TOOLS_AUTH_MANAGER: 'tools_auth_manager',
  REMOTE_MCP_SERVERS: 'remote_mcp_servers',
  EXTERNAL_PROVIDERS_ALL_ADMINS: 'external_providers_all_admins',
  // Per-user tool disable — lets an end user hide a specific tool from their
  // own tool lists while it stays enabled at the instance level.
  PER_USER_TOOL_DISABLE: 'per_user_tool_disable',
} as const;

export type FeatureFlagName = (typeof FeatureFlags)[keyof typeof FeatureFlags];

/**
 * Variables for the `workflow_ux` Optimizely feature flag.
 * Each key maps to a boolean variable that gates a specific workflow UX feature.
 */
export const WorkflowUxVariables = {
  NATURAL_LANGUAGE_CONDITIONS: 'natural_language_conditions',
  FIRST_CLASS_CONDITION_NODE: 'first_class_condition_node',
  STATIC_PARAMETERS: 'static_parameters',
  WORKFLOW_NESTING: 'workflow_nesting',
  WORKFLOW_BUILDER_UX_V2: 'workflow_builder_ux_v2',
  WORKFLOW_GLOBAL_SEARCH: 'workflow_global_search',
  CAPACITY_BANNER: 'capacity_banner',
  WORKFLOW_PRIORITY_OPTION_IN_TRIGGER: 'workflow_priority_option_in_trigger',
  HITL_NODE: 'hitl_node',
  HITL_ACTION_REQUIRED: 'hitl_action_required',
  SPOTLIGHT: 'spotlight',
  INTERACTIVE_STEPS: 'interactive_steps',
  PARALLEL_FOR_LOOP_TOGGLE: 'parallel_for_loop_toggle',
} as const;

/**
 * Variables for the `manage_instance_defaults` Optimizely feature flag.
 * Each key gates one instance-wide default an admin can set in Settings.
 */
export const ManageInstanceDefaultsVariables = {
  // Settings -> Chat Configuration, where an admin picks the CMP instance this
  // Opal instance acts in — approvals, tools, RAG, all of it. Distinct from
  // `workflow_ux.hitl_node`, which gates the approval node in the builder:
  // that is the authoring surface, this is where what it raises ends up.
  // Spec: docs/tech-spec/agent-framework/default-cmp-instance.md
  DEFAULT_CMP_INSTANCE: 'default_cmp_instance',
} as const;
